import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Switch,
  Space, Popconfirm, message, Card, Col, Divider, Row
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getBranches, createBranch, updateBranch, deleteBranch
} from '../api/branches';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import useAuthStore from '../store/authStore';
import PhoneInput from '../components/common/PhoneInput';
import { apiErrorMessage } from '../utils/apiError';
import { formatPhone } from '../utils/phone';

const Branches = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isBranchManager = user?.role === 'branch_manager';
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('branches', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  const fetchBranches = async (page = 1, searchText = debouncedSearch, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, search: searchText, sort_by: sortBy || undefined, sort_direction: sortDir || undefined };
      if (isBranchManager && user?.branch_id) params.id = user.branch_id;
      const response = await getBranches(params);
      setBranches(response.data.data);
      setPagination({
        current: response.data.current_page,
        pageSize: response.data.per_page,
        total: response.data.total,
      });
    } catch (error) {
      message.error('Şubeler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches(1, debouncedSearch);
  }, [debouncedSearch]);

  const handleSubmit = async (values) => {
    try {
      if (editingBranch) {
        await updateBranch(editingBranch.id, values);
        message.success('Şube güncellendi.');
      } else {
        await createBranch(values);
        message.success('Şube oluşturuldu.');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingBranch(null);
      fetchBranches();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    form.setFieldsValue({
      ...branch,
      // Laravel decimal:2 cast'i "10.00" gibi string donduruyor, InputNumber sayi bekliyor
      invoice_vat_rate: branch.invoice_vat_rate !== null && branch.invoice_vat_rate !== undefined
        ? Number(branch.invoice_vat_rate)
        : undefined,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteBranch(id);
      message.success('Şube silindi.');
      fetchBranches();
    } catch (error) {
      message.error('Şube silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Şube Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : null,
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      key: 'address',
      sorter: true,
      sortOrder: sortField === 'address' ? sortOrder : null,
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (v) => formatPhone(v) || '—',
      sorter: true,
      sortOrder: sortField === 'phone' ? sortOrder : null,
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      sortOrder: sortField === 'is_active' ? sortOrder : null,
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateBranch(record.id, { is_active: checked });
              message.success('Durum güncellendi.');
              fetchBranches();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<SettingOutlined />} onClick={() => navigate(`/branches/${record.id}`)} size="small">
            Yönet
          </Button>
          {!isBranchManager && (
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">
              Düzenle
            </Button>
          )}
          {!isBranchManager && (
            <Popconfirm
              title="Şubeyi silmek istediğinize emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button icon={<DeleteOutlined />} danger size="small">
                Sil
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Şube Yönetimi</h2>
        <Space>
          {!isBranchManager && (
            <Input
              placeholder="Şube ara..."
              allowClear
              style={{ width: 250 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          {!isBranchManager && <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingBranch(null);
              form.resetFields();
              setModalOpen(true);
            }}
          >
            Yeni Şube
          </Button>}

        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={branches}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchBranches(paginationInfo.current, debouncedSearch, paginationInfo.pageSize, sField, sDir);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Toplam ${total} şube`,
          }}
        />
      </Card>

      <Modal
        title={editingBranch ? 'Şube Düzenle' : 'Yeni Şube'}
        open={modalOpen}
        width={760}
        onCancel={() => {
          setModalOpen(false);
          setEditingBranch(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Şube Adı" rules={[{ required: true, message: 'Şube adı giriniz.' }]}>
            <Input placeholder="Duttosan Merkez" maxLength={100} />
          </Form.Item>
          <Form.Item name="address" label="Adres" extra="Faturada açık adres olarak kullanılır">
            <Input.TextArea placeholder="Cadde / sokak / kapı no" rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="district" label="İlçe">
                <Input placeholder="Aliağa" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="city" label="İl">
                <Input placeholder="İzmir" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="postal_code" label="Posta Kodu">
                <Input maxLength={10} placeholder="35800" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[{ pattern: /^\+\d{8,15}$/, message: 'Ülke kodunu seçip numarayı giriniz.' }]}
              >
                <PhoneInput placeholder="2321234567" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="E-posta"
                rules={[{ type: 'email', message: 'Geçerli bir e-posta girin.' }]}
              >
                <Input placeholder="muhasebe@ornek.com" />
              </Form.Item>
            </Col>
          </Row>
          {editingBranch && (
            <Form.Item name="is_active" label="Durum" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}

          {/* Faturanin SATICI tarafi. Her sube kendi adina fatura kestigi icin
              bu bilgiler sube bazinda tutuluyor. e-Arsiv il/ilceyi ayri alan
              olarak istedigi icin adres yukarida parcali giriliyor. */}
          <Divider titlePlacement="left" style={{ marginTop: 8 }}>Fatura Bilgileri</Divider>
          <p style={{ fontSize: 12, color: '#888', marginTop: -8, marginBottom: 16 }}>
            Bu şube adına kesilecek faturalarda görünür. KDV oranını mali müşavirinize teyit ettirin.
          </p>

          <Form.Item name="legal_title" label="Ticari Unvan" extra="Ticaret sicilindeki resmî unvan">
            <Input placeholder="Özel Aliağa Robotik Kodlama Kursu A.Ş." maxLength={200} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="tax_number"
                label="VKN"
                extra="Şahıs şirketiyse TC kimlik no"
                rules={[{ pattern: /^\d{10,11}$/, message: '10 veya 11 haneli olmalı.' }]}
              >
                <Input maxLength={11} placeholder="1234567890" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="tax_office" label="Vergi Dairesi">
                <Input placeholder="Aliağa" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="mersis_no" label="Mersis No" extra="Zorunlu değil">
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="trade_registry_no" label="Ticaret Sicil No" extra="Zorunlu değil">
                <Input maxLength={20} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item
                name="invoice_series_code"
                label="Fatura Seri Kodu"
                extra="En fazla 5 karakter"
                rules={[{ pattern: /^[A-Za-z0-9]{1,5}$/, message: 'En fazla 5 harf/rakam.' }]}
              >
                <Input maxLength={5} placeholder="ARY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="invoice_vat_rate" label="KDV Oranı (%)">
                <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="10" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="invoice_item_name" label="Varsayılan Hizmet Adı">
                <Input placeholder="Robotik Kodlama Eğitim Hizmeti" maxLength={200} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => {
                setModalOpen(false);
                setEditingBranch(null);
                form.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingBranch ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Branches;